<template>
    <div class="visitor-search">
        <div class="search-input-container">
            <input
                type="text"
                v-model="searchQuery"
                @input="handleSearch"
                @focus="showDropdown = true"
                @blur="handleBlur"
                :placeholder="placeholder"
                class="search-input"
            >
        </div>

        <div v-if="showDropdown" class="search-dropdown">
            <div v-if="loading" class="dropdown-item loading">
                Searching...
            </div>
            
            <template v-else>
                <!-- Show matching visitors -->
                <div 
                    v-for="visitor in filteredResults" 
                    :key="visitor.id"
                    class="dropdown-item"
                    @mousedown="selectVisitor(visitor)"
                >
                    <div class="visitor-info">
                        <span class="visitor-name">
                            {{ visitor.first_name }} {{ visitor.last_name }}
                        </span>
                        <span class="visitor-details" v-if="visitor.phone || visitor.company">
                            ({{ [visitor.phone, visitor.company].filter(Boolean).join(' - ') }})
                        </span>
                    </div>
                </div>

                <!-- No results message -->
                <div 
                    v-if="!searchQuery && !filteredResults.length" 
                    class="dropdown-item empty"
                >
                    Type to search visitors
                </div>

                <!-- Add New Visitor button -->
                <div 
                    v-if="searchQuery && !loading"
                    class="dropdown-item add-new"
                    @mousedown="showAddVisitorModal = true"
                >
                    <span class="add-icon">+</span>
                    Add New Visitor
                </div>
            </template>
        </div>

        <!-- Add New Visitor Modal -->
        <div v-if="showAddVisitorModal" class="modal">
            <div class="modal-content">
                <h3>Add New Visitor</h3>
                
                <div class="form-group">
                    <label>First Name</label>
                    <input 
                        type="text" 
                        v-model="newVisitor.first_name"
                        class="form-input"
                    >
                </div>
                
                <div class="form-group">
                    <label>Last Name</label>
                    <input 
                        type="text" 
                        v-model="newVisitor.last_name"
                        class="form-input"
                    >
                </div>
                
                <div class="form-group">
                    <label>Email</label>
                    <input 
                        type="email" 
                        v-model="newVisitor.email"
                        class="form-input"
                    >
                </div>
                
                <div class="form-group">
                    <label>Phone</label>
                    <div class="phone-input">
                        <select v-model="phoneCountry" class="country-select">
                            <option value="+61">🇦🇺 +61</option>
                            <option value="+64">🇳🇿 +64</option>
                        </select>
                        <input 
                            v-model="phoneNumber" 
                            type="tel" 
                            class="form-input phone"
                            placeholder="412 345 678"
                        >
                    </div>
                </div>
                
                <div class="form-group">
                    <label>Company</label>
                    <input 
                        type="text" 
                        v-model="newVisitor.company"
                        class="form-input"
                    >
                </div>

                <div class="modal-actions">
                    <button 
                        @click="showAddVisitorModal = false" 
                        class="cancel-btn"
                    >
                        Cancel
                    </button>
                    <button 
                        @click="createVisitor" 
                        class="submit-btn"
                        :disabled="isCreating"
                    >
                        {{ isCreating ? 'Creating...' : 'Create Visitor' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { visitorService } from '../services/visitorService';

export default {
    name: 'VisitorSearchCombo',
    props: {
        placeholder: {
            type: String,
            default: 'Search for visitors...'
        },
        disabledVisitors: {
            type: Array,
            default: () => []
        }
    },
    emits: ['visitor-selected'],
    setup(props, { emit }) {
        const searchQuery = ref('');
        const searchResults = ref([]);
        const showDropdown = ref(false);
        const loading = ref(false);
        let searchTimeout;
        const showAddVisitorModal = ref(false);
        const isCreating = ref(false);
        const phoneCountry = ref('+61');
        const phoneNumber = ref('');
        const newVisitor = ref({
            first_name: '',
            last_name: '',
            email: '',
            company: ''
        });

        watch([phoneCountry, phoneNumber], ([country, number]) => {
            if (number) {
                newVisitor.value.phone = country + number.replace(/\s/g, '');
            } else {
                newVisitor.value.phone = '';
            }
        });

        const handleSearch = async () => {
            if (!searchQuery.value) {
                searchResults.value = [];
                return;
            }

            clearTimeout(searchTimeout);
            loading.value = true;
            
            searchTimeout = setTimeout(async () => {
                try {
                    const results = await visitorService.searchVisitors(searchQuery.value);
                    searchResults.value = results;
                } catch (error) {
                    console.error('Search error details:', {
                        message: error.message,
                        config: error.config,
                        response: error.response?.data
                    });
                    searchResults.value = [];
                } finally {
                    loading.value = false;
                }
            }, 300);
        };

        const selectVisitor = (visitor) => {
            emit('visitor-selected', visitor);
            searchQuery.value = '';
            searchResults.value = [];
            showDropdown.value = false;
        };

        const handleBlur = () => {
            // Delay hiding dropdown to allow click events to fire
            setTimeout(() => {
                showDropdown.value = false;
            }, 200);
        };

        const filteredResults = computed(() => {
            const disabledNames = new Set(props.disabledVisitors.map(v => 
                `${v.first_name} ${v.last_name}`.toLowerCase()
            ));

            const filtered = searchResults.value.filter(visitor => {
                const visitorName = `${visitor.first_name} ${visitor.last_name}`.toLowerCase();
                return !disabledNames.has(visitorName);
            });

            return filtered;
        });

        const createVisitor = async () => {
            if (isCreating.value) return;
            isCreating.value = true;
            
            try {
                const visitor = await visitorService.createVisitor(newVisitor.value);
                emit('visitor-selected', visitor);
                showAddVisitorModal.value = false;
                newVisitor.value = { first_name: '', last_name: '', email: '', company: '' };
                phoneCountry.value = '+61';
                phoneNumber.value = '';
            } catch (error) {
                console.error('Error creating visitor:', error);
            } finally {
                isCreating.value = false;
            }
        };

        return {
            searchQuery,
            filteredResults,
            showDropdown,
            loading,
            handleSearch,
            selectVisitor,
            handleBlur,
            showAddVisitorModal,
            isCreating,
            newVisitor,
            createVisitor,
            phoneCountry,
            phoneNumber
        };
    }
};
</script>

<style scoped>
.visitor-search {
    position: relative;
    width: 100%;
}

.search-input-container {
    position: relative;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
}

.search-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-top: 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.dropdown-item {
    padding: 0.5rem;
    cursor: pointer;
    text-align: left;
    display: flex;
    justify-content: flex-start;
    align-items: center;
}

.dropdown-item:hover {
    background-color: #f5f5f5;
}

.dropdown-item.loading,
.dropdown-item.empty {
    color: #666;
    font-style: italic;
    cursor: default;
}

.visitor-name {
    font-weight: 500;
    text-align: left;
}

.visitor-company {
    font-size: 0.8rem;
    color: #666;
    text-align: left;
}

.visitor-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.company-label {
    color: #666;
    font-size: 0.8rem;
}

.add-new {
    color: #dc3545;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.add-icon {
    font-size: 1.2rem;
    font-weight: bold;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.form-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
}

/* Reuse modal styles from CheckInModal */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
}

.modal-actions {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
}

.cancel-btn, .submit-btn {
    padding: 0.5rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
}

.cancel-btn {
    background: #6c757d;
    color: white;
}

.submit-btn {
    background: #4285f4;
    color: white;
}

.submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.cancel-btn:hover {
    background: #5a6268;
}

.submit-btn:hover:not(:disabled) {
    background: #357abd;
}

.phone-input {
    display: flex;
    gap: 0.5rem;
}

.country-select {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    min-width: 100px;
}

.phone-input .form-input.phone {
    flex: 1;
}
</style> 